<template>
  <div>
  <PatternProperties
      :colors="patternColors"
      :patternsize="currentSizeStep"
      :pattern_name_localized="patternnameLocalized"
      :pattern_previewimage="patternVorschaubild"
      @minus="decreasePatternSize"
      @plus="increasePatternSize"
      @colorchosen="patternColorPickerClicked"
      @opencolortips="openColortips"
  />
  <ColorShadeGroupSelection
      :currentColorValueIntOrNONEstring="currentColorValueInt"
      :animationkeyframesgetter="colorGroupAnimation"
      containerclass="color-pattern__shadegroup"
      hasNoColorOption="false"
      :palettenCluster="palettenClusterSorted"
      @setColor="setColor"
      :currentpalette="currentPalette"
  />
  <ColorShadeSelection
      :currentColorValueIntOrNONEstring="currentColorValueInt"
      :animationkeyframesgetter="colorShadeAnimation"
      containerclass="color-pattern__shade"
      has-no-color-option="false"
      :palettenCluster="palettenClusterSorted"
      @setColor="setColor"
      :currentpalette="currentPalette"
  />
  <PatternFarbvorschlaege :farbvorschlaege="farbvorschlaege" :logo_id="stocklogoID"

                          @colortip_clicked="farbvorschlaggewaehlt"/>
  </div>
</template>

<script lang="ts" setup>

import {EventVerteiler} from "@k/global/eventVerteiler";
import {PATTERN_EVENTNAMES} from "@k/js/pattern/patternFactory";
import {PatternInEntwurf} from "@k/js/pattern/patternEntwurfsobjektInteractor"
import {PatternSizeSteps} from "@k/js/pattern/patternSizeSteps"
import {StockLogoColorVersion} from "@k/js/pattern/patternFarbVorschlaege"
import {computed, ComputedRef, Ref, ref,} from "vue";
import {useStore} from "vuex";
import {AnimationWaitingForTargetFactory, KeyFrameTargetValues} from "@km/animations/animationFactory";
import ColorShadeSelection from "@km/components/color/ColorShadeSelection.vue";
import {COLORMODULE_POSSIBLE_STATES, setColorModuleState} from "@km/components/color/ModuleColorAnimationStates";
import {ChromoLogoColor} from "@k/js/selectableObjects";
import {PalettenEintragFromDBWithFarbgruppenEinteilung} from "@k/js/databaseInterfaceClasses"
import {ColorUtils} from "@k/js/colorUtils";
import PatternFarbvorschlaege from "@km/components/patternedit/PatternFarbvorschlaege.vue";
import ColorShadeGroupSelection from "@km/components/color/ColorShadeGroupSelection.vue";
import PatternProperties from "@km/components/patternedit/PatternProperties.vue";
import {PatternPreviewImageHolder} from "@km/store/ColorDataGetterFromResourceStore";
import {useColoreditStore} from "@km/components/color/colorEditStatusStore";
import {useModuleColorOpenStateStore} from "@km/store/frontendstate/useModuleColorOpenState";

function getStockLogo(store, id) {
  return store.getters["StockLogoStore/getLogo"](parseInt(id))
}

function getStepSizeContainer(store, pattern: PatternInEntwurf) {
  const stockLogo = getStockLogo(store, pattern.stockLogoID)
  return new PatternSizeSteps(stockLogo.minSizeCM, stockLogo.maxSizeCM, stockLogo.standardSizeCM);
}

function getColorShadeAnimation() {
  const store = useStore();
  const animation = AnimationWaitingForTargetFactory.getFadeInOutAnimation(".color-pattern__shade");
  const keyframes = new KeyFrameTargetValues({targetValue: 9999, opacity: 1}, {targetValue: -9999, opacity: 0});
  const openStatusStore = useModuleColorOpenStateStore();
  const getterForWatch = () => openStatusStore.getPatternColorShadeSelection;
  return {animation, keyframes, getterForWatch}
}

function getColorShadeGroupAnimation() {
  const store = useStore();
  const animation = AnimationWaitingForTargetFactory.getLeftRightAnimationpercentWithOpacityAnimation(".color-pattern__shadegroup");
  const keyframes = new KeyFrameTargetValues({targetValue: -100, opacity: 1}, {targetValue: 100, opacity: 0});
  const openStatusStore = useModuleColorOpenStateStore();
  const getterForWatch = () => openStatusStore.getPatternColorShadeGroupSelection;
  return {animation, keyframes, getterForWatch}
}

function getCurrentSizeStepFromPattern(store, pattern: PatternInEntwurf) {
  const sizeSteps = getStepSizeContainer(store, pattern);
  return sizeSteps.getSizeStepNumber(pattern.tileWidthInCM);
}

function getFarbVorschlaege(store, stockLogoID) {
  const stockLogo = getStockLogo(store, stockLogoID);
  return stockLogo.colorVersions;
}

const statusStore = useColoreditStore();
const currentColorCode = computed(() => statusStore.getCurrentColorCode);

const store = useStore();
const patternToEdit = computed(() => store.getters.getPattern(currentColorCode.value)) as ComputedRef<null | PatternInEntwurf>;
const currentSizeStep = computed(() => patternToEdit.value === null ? -1 : getCurrentSizeStepFromPattern(store, patternToEdit.value));
const currentPatternColor = ref(null) as Ref<ChromoLogoColor | null>
const currentColorValueInt = computed(() => currentPatternColor.value === null ? -1 : currentPatternColor.value.colorIntValue);
const colorCodeNr = computed(() => store.getters.getColorCodeNr(currentColorCode.value));
const currentPalette = computed(() => currentPatternColor.value?.palette)
const patternColorPickerClicked = (c: ChromoLogoColor) => {
  currentPatternColor.value = c;
  setColorModuleState(useModuleColorOpenStateStore(), COLORMODULE_POSSIBLE_STATES.patterncolorIndividualColorSelection);
}
const sendSizeChangeEvent = (plusminus: "plus" | "minus") => {
  EventVerteiler.broadcast(
      plusminus === "minus" ? PATTERN_EVENTNAMES.CHANGESIZEDOWN_REQUEST : PATTERN_EVENTNAMES.CHANGESIZEUP_REQUEST,
      {colorCodeNr: colorCodeNr.value}
  )
}

const setColor = (color: PalettenEintragFromDBWithFarbgruppenEinteilung) => {
  EventVerteiler.broadcast(PATTERN_EVENTNAMES.REQUEST_COLOR_CHANGE, {
    colorCodeOnEntwurf: currentColorCode.value,
    colorCodeInPattern: (currentPatternColor.value as ChromoLogoColor).colorCode,
    newColorHexValue: color.RGBHex,
    oldColorHexValue: ColorUtils.decToHex((currentPatternColor.value as ChromoLogoColor).colorIntValue)
  })
}

const farbvorschlaggewaehlt = (colorversion: StockLogoColorVersion) => {
  EventVerteiler.broadcast(PATTERN_EVENTNAMES.COLORVERSION_CLICKED, {
    forColorCodeNr: colorCodeNr.value,
    colorversionID: colorversion.id,
    stockLogoID: parseInt(patternToEdit.value?.stockLogoID)
  })
};
const palettenCluster = computed(() => store.getters.getPalettenCluster("CM5 Bedruckbar"));


const patternColors = computed(() => patternToEdit.value?.colors || []);
const stocklogoID = computed(() => parseInt(patternToEdit.value?.stockLogoID) || -1);
const farbvorschlaege = computed(() => patternToEdit.value !== null ? getFarbVorschlaege(store, patternToEdit.value.stockLogoID) : []);
const decreasePatternSize = () => sendSizeChangeEvent("minus");
const increasePatternSize = () => sendSizeChangeEvent("plus");
const palettenClusterSorted = computed(() => store.getters.getPalettenClusterSorted(palettenCluster.value));
const colorGroupAnimation = getColorShadeGroupAnimation();
const colorShadeAnimation = getColorShadeAnimation();

const openColortips = () => setColorModuleState(useModuleColorOpenStateStore(), COLORMODULE_POSSIBLE_STATES.patternFarbvorschlaege);
const patternnameLocalized = computed(() => store.getters["StockLogoStore/getLogo"](patternToEdit.value?.stockLogoID)?.nameLocalized || "");
const patternVorschaubild = computed(() => PatternPreviewImageHolder.getPreviewImage(store.getters.getColorCodeNr(currentColorCode.value)).value || "");


</script>

<style scoped>

</style>
